import useStateValue from "frontend/state/value";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import React, { useEffect, useState } from "react";
import {
  Board,
  BoardPermission,
  BoardPermissionAccessTitles,
  BoardPermissionFullTitles,
  isPublicPermission,
} from "shared/datamodel/schemas/board";
import style from "./share-canvas-modal-new.module.css";
// eslint-disable-next-line unicorn/prefer-node-protocol
import { setTimeout } from "timers";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import classNames from "classnames";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import useAbility from "frontend/hooks/use-ability";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { PasswordInputSetter } from "frontend/ui-components/password-input";
import { VIcon } from "frontend/icons/v-icon";
import { XIcon } from "frontend/icons/x-icon";
import { ClosedLock } from "frontend/icons/closedLock";
import { OpenLock } from "frontend/icons/openLock";
import Checkbox from "frontend/ui-components/checkbox";
import { PlanFeatureName } from "shared/datamodel/schemas";

export default function InviteToBoardByLink({
  board,
  onChangePermission,
  onPasswordSet,
  passwordChecked,
  setPasswordChecked,
}: {
  board: Board;
  onChangePermission: (permission: BoardPermission) => void;
  onPasswordSet: (password?: string | null) => void;
  passwordChecked: boolean;
  setPasswordChecked: (checked: boolean) => void;
}) {
  const [sharable, setSharable] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [{ user, account }] = useStateValue();
  const isAnonymousEnabled = usePlanFeatureValue(PlanFeatureName.ANONYMOUS_ENABLED);
  const { canEditInstance, canInviteToInstance } = useAbility();
  const userPermissionString = canEditInstance(user, board, InstanceType.Board) ? "editor" : "viewer";
  const canInvite = canInviteToInstance(user, board, InstanceType.Board) ? "can-invite" : "cannot-invite";
  const minimumPasswordLength = 8;

  useEffect(() => {
    if (board) {
      setSharable(isPublicPermission(board.permission));
    }
  }, [board]);

  function getBoardPermission() {
    return sharable || !account ? "public" : `private`;
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(`${location.origin}${getPathPrefix("/d/")}${board!.documentId}`);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 2000);
    //Due to some vodu, it stack the UI
    //tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.INVITE_LINK_COPIED);
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ADMIN_ACTION,
      "invite_link_copied",
      "board permission",
      getBoardPermission(),
      userPermissionString,
      canInvite
    );
  }

  function renderPermissionOption({ option }: { option: BoardPermission }) {
    const shouldUpgrade = option === BoardPermission.public && !isAnonymousEnabled;
    const element = (
      <div className={classNames(style.option, { [style.disabled]: shouldUpgrade })}>
        <span className={style.optionTitle}>{`${BoardPermissionAccessTitles[option]}`}</span>
      </div>
    );
    return {
      element,
      enabled: !shouldUpgrade,
      customStyle: { minWidth: 102, padding: "0 4px" },
      handlesSelectedState: true,
    };
  }

  function onPasswordCheckboxChange() {
    setPasswordChecked(!passwordChecked);
    const shouldRemovePasswordProtected = passwordChecked;
    if (shouldRemovePasswordProtected) {
      onPasswordSet(null);
    }
  }

  function renderPermissionPicker() {
    if (!board.isOwner) {
      return (
        <span className={style.subtitle} style={{ marginLeft: 25 }}>
          {BoardPermissionFullTitles[board.permission]}
        </span>
      );
    }
    return (
      <div className={style.publicPermissionContainer}>
        <div className={classNames(style.linkPermissionPContainer, style.isPasswordProtected)}>
          <div style={{ width: 197 }}>
            <span className={style.textInOption}>Anyone with a link</span>
          </div>
          <div className={style.pickerContainer}>
            <DropdownPicker
              options={[BoardPermission.public, BoardPermission.publicReadOnly]}
              titles={(v) => BoardPermissionAccessTitles[v]}
              optionRenderer={renderPermissionOption}
              onChange={onChangePermission}
              isSelected={(v) => board?.permission === v}
              enabled={true}
              pickerCustomStyle={{ height: 31, border: "none" }}
              optionsPickerStyle={{ fontSize: 12, width: 102 }}
            />
          </div>
        </div>
        <div className={style.passwordCheck}>
          <Checkbox
            checked={passwordChecked}
            onChange={onPasswordCheckboxChange}
            customStyle={{ height: 20, width: 20 }}
          />
          <span style={{ paddingTop: 6 }}>{passwordChecked ? <ClosedLock /> : <OpenLock />}</span>
          <span>Set Password</span>
        </div>
      </div>
    );
  }

  function renderValidationMessage(hasError?: boolean, passwordLength?: number) {
    if (passwordLength && passwordLength > minimumPasswordLength) {
      return (
        <div className={`${style.validationMessage} ${style.success}`}>
          <span className={style.validationIcon}>
            <VIcon width="11" height="9" viewBox="0 0 11 9" fill="none" color={"#00B875"} />
          </span>
          <span>Min {minimumPasswordLength} characters</span>
        </div>
      );
    }
    if (hasError) {
      return (
        <div className={`${style.validationMessage} ${style.error}`}>
          <span className={style.validationIcon}>
            <XIcon width="10" height="10" viewBox="0 0 10 10" fill="none" color={"#F05C30"} />
          </span>
          <span>Min {minimumPasswordLength} characters</span>
        </div>
      );
    }
    return <></>;
  }

  function renderPasswordInput() {
    const showPasswordContainer = passwordChecked && board.isOwner;
    return (
      <>
        {showPasswordContainer ? (
          <div className={style.passwordContainer}>
            <PasswordInputSetter
              onPasswordSet={onPasswordSet}
              renderValidationMessage={renderValidationMessage}
              isPasswordProtected={board.isPasswordProtected}
            />
          </div>
        ) : null}
      </>
    );
  }

  function renderInvite() {
    return (
      <div className={style.shareOptionContent}>
        <div className={style.inviteByLinkContainer}>
          {renderPermissionPicker()}
          {passwordChecked && renderPasswordInput()}
          <div className={style.shareLink} data-testid="copylink">
            <StyledButton
              icon={getPathPrefix("/images/share-icon-bold.svg")}
              title={showCopySuccess ? "Link Copied!" : "Copy Link"}
              onClick={handleCopyLink}
              style={ButtonStyle.filled}
              customStyle={{
                minWidth: 139,
                height: 33,
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: "Poppins",
                borderRadius: 6,
              }}
            />
            <span>
              {sharable
                ? "Anyone with the link can access this canvas"
                : "Only people with access can open with the link"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return renderInvite();
}
